import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

const EmployeeGoalsReviews = () => {
  const baseUrl = API_BASE_URL;
  const [goals, setGoals] = useState([]);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const employeeId = location.state?.employeeId || id;
  const [employeeName, setEmployeeName] = useState('');

  // Year filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filteredGoals, setFilteredGoals] = useState([]);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [agreedModalOpen, setAgreedModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const [selectedGoal, setSelectedGoal] = useState(null);
  const [viewGoal, setViewGoal] = useState(null);
  const [hodScore, setHodScore] = useState('');
  const [agreedScore, setAgreedScore] = useState('');
  const [modalError, setModalError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Helper function to filter goals by year
  const filterGoalsByYear = (goalsList, year) => {
    return goalsList.filter(goal => {
      if (goal.year !== undefined && goal.year !== null) {
        return Number(goal.year) === Number(year);
      }
      const goalDate = goal.created_at || goal.updated_at || goal.start_date || goal.end_date || goal.date_created;
      if (!goalDate) return true;
      return new Date(goalDate).getFullYear() === Number(year);
    });
  };

  // Document URL Resolver
  const resolveDocumentUrl = (goal) => {
    if (!goal) return null;
    const candidates = [
      goal?.goal_documents,
      goal?.supporting_document_url,
      goal?.document,
      goal?.document_url,
      goal?.file,
      goal?.file_url,
      goal?.attachment,
      goal?.attachment_url,
      goal?.proof_document,
      goal?.goal_document,
      goal?.goal_document_url,
      Array.isArray(goal?.documents) ? goal.documents[0]?.document : null,
      Array.isArray(goal?.documents) ? goal.documents[0]?.url : null,
      Array.isArray(goal?.goal_documents) ? goal.goal_documents[0]?.document : null,
      Array.isArray(goal?.goal_documents) ? goal.goal_documents[0]?.document_url : null,
      Array.isArray(goal?.progress_history)
        ? goal.progress_history.find((item) => item?.document || item?.document_url)?.document ||
          goal.progress_history.find((item) => item?.document || item?.document_url)?.document_url
        : null,
    ].filter(Boolean);

    const raw = candidates.find((value) => typeof value === 'string' && value.trim() !== '');
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('/')) return `${baseUrl}${raw}`;
    return `${baseUrl}/${raw}`;
  };

  useEffect(() => {
    const fetchGoals = async () => {
      if (!employeeId) return;
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const empData = response?.data?.employee || response?.data || {};
        const completedGoals = (Array.isArray(empData?.goals) ? empData?.goals : []).filter(
          goal => goal.status === 'complete' || goal.status === 'completed'
        );
        setGoals(completedGoals);
        setEmployeeName(empData?.name || empData?.full_name || empData?.user?.full_name || '');

        const yearFiltered = filterGoalsByYear(completedGoals, selectedYear);
        setFilteredGoals(yearFiltered);
      } catch (err) {
        console.error('Error fetching employee KPIs:', err);
        setError('Failed to fetch KPIs.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoals();
  }, [employeeId, baseUrl]);

  useEffect(() => {
    if (goals.length > 0) {
      setFilteredGoals(filterGoalsByYear(goals, selectedYear));
    } else {
      setFilteredGoals([]);
    }
  }, [selectedYear, goals]);

  // Handlers for score reviews and modal opens
  const handleReviewClick = (goal) => {
    setSelectedGoal(goal);
    setHodScore('');
    setModalError('');
    setModalOpen(true);
  };

  const handleAgreedReviewClick = (goal) => {
    setSelectedGoal(goal);
    setAgreedScore('');
    setModalError('');
    setAgreedModalOpen(true);
  };

  const handleViewClick = (goal) => {
    setViewGoal(goal);
    setViewModalOpen(true);
  };

  // Submit HOD Score
  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    const maxScore = Number(selectedGoal.metric || 100);
    const parsedScore = Number(hodScore);
    if (isNaN(parsedScore) || parsedScore < 0) {
      setModalError('Please enter a valid score.');
      return;
    }
    if (parsedScore > maxScore) {
      setModalError(`HOD score cannot be greater than the goal metric (${maxScore}).`);
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      const token = Cookies.get('token');
      await axios.post(
        `${baseUrl}/api/goals/${selectedGoal.id}/score/`,
        { hod_score: parsedScore },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const updateList = (list) => list.map(g => g.id === selectedGoal.id ? { ...g, hod_score: parsedScore } : g);
      setGoals(prev => updateList(prev));
      setFilteredGoals(prev => updateList(prev));
      setModalOpen(false);
      setSelectedGoal(null);
      setHodScore('');
    } catch (err) {
      console.error('Error submitting HOD score:', err);
      setModalError('Failed to submit HOD score.');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Agreed Score
  const handleAgreedScoreSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    const maxScore = Number(selectedGoal.metric || 100);
    const parsedScore = Number(agreedScore);
    if (isNaN(parsedScore) || parsedScore < 0) {
      setModalError('Please enter a valid score.');
      return;
    }
    if (parsedScore > maxScore) {
      setModalError(`Agreed score cannot be greater than the KPI metric (${maxScore}).`);
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      const token = Cookies.get('token');
      await axios.post(
        `${baseUrl}/api/goals/${selectedGoal.id}/score/`,
        { agreed_score: parsedScore },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const updateList = (list) => list.map(g => g.id === selectedGoal.id ? { ...g, agreed_score: parsedScore } : g);
      setGoals(prev => updateList(prev));
      setFilteredGoals(prev => updateList(prev));
      setAgreedModalOpen(false);
      setSelectedGoal(null);
      setAgreedScore('');
    } catch (err) {
      console.error('Error submitting agreed score:', err);
      setModalError('Failed to submit agreed score.');
    } finally {
      setSubmitting(false);
    }
  };

  // Generate Report Data for PDF
  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });

    const totalScore = filteredGoals.reduce((sum, g) => {
      const finalScore = g.agreed_score !== undefined && g.agreed_score !== null
        ? Number(g.agreed_score)
        : (Number(g.hod_score) || 0);
      return sum + finalScore;
    }, 0);
    const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
    const percentage = totalMetric > 0 ? ((totalScore / totalMetric) * 100).toFixed(2) : '0.00';

    return {
      employeeName,
      year: selectedYear,
      kras: Object.entries(kraMap),
      totalScore,
      totalMetric,
      percentage,
      goals: filteredGoals
    };
  };

  // PDF Export Functionality
  const generatePDF = async () => {
    try {
      const reportData = generateReportData();
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');

      const loadLogoDataUrl = async () => {
        try {
          const response = await fetch('/celebration logo.png');
          if (!response.ok) return null;
          const blob = await response.blob();
          return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          return null;
        }
      };

      const logoDataUrl = await loadLogoDataUrl();
      const drawWatermark = (pageNumber = 1) => {
        if (!logoDataUrl) return;
        doc.setPage(pageNumber);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const wmWidth = 120;
        const wmHeight = 90;
        const x = (pageWidth - wmWidth) / 2;
        const y = (pageHeight - wmHeight) / 2;
        doc.addImage(logoDataUrl, 'PNG', x, y, wmWidth, wmHeight);
      };

      const primaryColor = [59, 130, 246];
      const darkGray = [55, 65, 81];
      const lightGray = [156, 163, 175];

      const wrapText = (text, maxWidth) => {
        const words = String(text || '').split(' ');
        const lines = [];
        let currentLine = '';

        for (let word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const testWidth = doc.getTextWidth(testLine);
          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines;
      };

      const cssColorToRgb = (color) => {
        if (!color || typeof color !== 'string') return [229, 231, 235];
        const value = color.trim();
        const hexMatch = value.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
        if (hexMatch) {
          let hex = hexMatch[1];
          if (hex.length === 3) hex = hex.split('').map(ch => ch + ch).join('');
          return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16)
          ];
        }
        const rgbMatch = value.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (rgbMatch) {
          return [
            Math.max(0, Math.min(255, parseInt(rgbMatch[1], 10))),
            Math.max(0, Math.min(255, parseInt(rgbMatch[2], 10))),
            Math.max(0, Math.min(255, parseInt(rgbMatch[3], 10)))
          ];
        }
        return [229, 231, 235];
      };

      const drawTable = (headers, rows, startY, maxWidth = 180) => {
        const colWidths = [58, 38, 20, 28, 36];
        const rowHeight = 8;
        const headerHeight = 10;

        doc.setFillColor(...primaryColor);
        doc.rect(15, startY, maxWidth, headerHeight, 'F');

        let x = 15;
        headers.forEach((header, i) => {
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(header, x + 2, startY + 6);
          x += colWidths[i];
        });

        let currentY = startY + headerHeight;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);

        rows.forEach((row, rowIndex) => {
          if (rowIndex % 2 === 0) {
            doc.setFillColor(249, 250, 251);
            doc.rect(15, currentY, maxWidth, rowHeight, 'F');
          }

          x = 15;
          let maxLines = 1;
          row.forEach((cell, colIndex) => {
            if (cell && typeof cell === 'object' && cell.type === 'rag') {
              const ragLabel = String(cell.label || '');
              const [r, g, b] = cssColorToRgb(cell.color);
              const boxX = x + 2;
              const boxY = currentY + 2.2;
              const boxW = 8;
              const boxH = 4;
              doc.setFillColor(r, g, b);
              doc.rect(boxX, boxY, boxW, boxH, 'F');
              doc.setDrawColor(203, 213, 225);
              doc.rect(boxX, boxY, boxW, boxH);

              const wrappedText = wrapText(ragLabel, colWidths[colIndex] - 13);
              maxLines = Math.max(maxLines, wrappedText.length || 1);
              wrappedText.forEach((line, lineIndex) => {
                doc.text(line, x + 12, currentY + 5 + (lineIndex * 3.5));
              });
            } else {
              const cellText = String(cell || '');
              const wrappedText = wrapText(cellText, colWidths[colIndex] - 4);
              maxLines = Math.max(maxLines, wrappedText.length || 1);
              wrappedText.forEach((line, lineIndex) => {
                doc.text(line, x + 2, currentY + 5 + (lineIndex * 3.5));
              });
            }

            x += colWidths[colIndex];
          });

          currentY += Math.max(rowHeight, maxLines * 3.5 + 2);
        });

        return currentY;
      };

      let yPosition = 20;
      drawWatermark(1);

      doc.setTextColor(...primaryColor);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Report', 105, yPosition, { align: 'center' });

      yPosition += 12;
      doc.setTextColor(...darkGray);
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      doc.text(`for ${employeeName || 'Employee'} - Year: ${selectedYear}`, 105, yPosition, { align: 'center' });

      yPosition += 10;
      doc.setFillColor(248, 250, 252);
      doc.rect(15, yPosition, 180, 25, 'F');
      doc.setDrawColor(...primaryColor);
      doc.rect(15, yPosition, 180, 25);

      doc.setTextColor(...darkGray);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Summary', 20, yPosition + 6);

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Total Final Score: ${reportData.totalScore} / ${reportData.totalMetric}`, 20, yPosition + 12);
      doc.text(`Performance Percentage: ${reportData.percentage}% | Goals Count: ${reportData.goals.length}`, 20, yPosition + 18);

      yPosition += 32;

      reportData.kras.forEach(([kraName, kraGoals]) => {
        const kraTotal = kraGoals.reduce((s, g) => {
          const finalScore = g.agreed_score !== undefined && g.agreed_score !== null
            ? Number(g.agreed_score)
            : (Number(g.hod_score) || 0);
          return s + finalScore;
        }, 0);
        const kraMetric = kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0);

        if (yPosition > 240) {
          doc.addPage();
          drawWatermark(doc.internal.getNumberOfPages());
          yPosition = 20;
        }

        doc.setFillColor(...primaryColor);
        doc.rect(15, yPosition, 180, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(`KRA: ${kraName} (${kraTotal}/${kraMetric})`, 18, yPosition + 7);

        yPosition += 12;

        const headers = ['Activity/KPI', 'Standard', 'Metric', 'RAG', 'Final Score'];
        const rows = kraGoals.map(goal => {
          const finalScore = goal.agreed_score !== undefined && goal.agreed_score !== null
            ? goal.agreed_score
            : goal.hod_score;

          return [
            goal.activity || 'N/A',
            goal.performance_standard || 'N/A',
            goal.metric || 'N/A',
            {
              type: 'rag',
              label: goal.rag_color || '',
              color: goal.progress_indicator || '#e5e7eb'
            },
            finalScore !== undefined ? finalScore : 'Not Reviewed',
          ];
        });

        rows.push(['KRA Total', '', '', '', `${kraTotal}/${kraMetric}`]);
        yPosition = drawTable(headers, rows, yPosition);
        yPosition += 12;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(...lightGray);
        doc.setFontSize(8);
        doc.text('Generated by Performance Management System', 15, 290);
        doc.text(`Page ${i} of ${pageCount}`, 175, 290, { align: 'right' });
      }

      doc.save(`Performance_Report_${employeeName || 'Employee'}_${selectedYear}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  // Group goals by KRA
  const kraGroupedGoals = (() => {
    const kraMap = {};
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });
    return Object.entries(kraMap);
  })();

  const totalScore = filteredGoals.reduce((sum, g) => {
    const gScore = g.agreed_score !== undefined && g.agreed_score !== null
      ? Number(g.agreed_score)
      : (Number(g.hod_score) || 0);
    return sum + gScore;
  }, 0);
  const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
  const totalPercentage = totalMetric > 0 ? ((totalScore / totalMetric) * 100).toFixed(2) : '0.00';

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header & Breadcrumbs */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2 mb-md-0">
              <div className="d-flex align-items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                >
                  <i className="ti ti-arrow-left fs-16"></i>
                  <span>Back</span>
                </button>
                <div>
                  <h4 className="mb-0">Employee KPIs Review</h4>
                  <p className="text-muted mb-0 fs-13">Review completed employee KPIs and assign HOD scores</p>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              {/* Year Filter */}
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0 fw-medium fs-14">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* PDF Export */}
              {filteredGoals.length > 0 && (
                <button
                  onClick={generatePDF}
                  className="btn btn-danger btn-sm d-flex align-items-center gap-2 shadow-sm"
                  title="Download PDF Report"
                >
                  <i className="ti ti-file-type-pdf fs-18"></i>
                  <span>Download Report</span>
                </button>
              )}

              {/* Employee Badge */}
              {employeeName && (
                <span className="badge bg-primary-transparent text-primary fs-14 py-2 px-3 fw-semibold border border-primary-light">
                  <i className="ti ti-user me-1"></i> {employeeName}
                </span>
              )}
            </div>
          </div>

          {/* Table Container */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading KPIs...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger m-3">{error}</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Activity / Key Performance Indicator</th>
                        <th>Performance Standard / Target</th>
                        <th>RAG Indicator</th>
                        <th className="text-center">KPI Metric</th>
                        <th className="text-center">HOD Score</th>
                        <th className="text-center">Agreed Score</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGoals.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            No completed KPIs found for year {selectedYear}.
                          </td>
                        </tr>
                      ) : (
                        kraGroupedGoals.map(([kra, kraGoals], gidx) => {
                          const kraSubtotalScore = kraGoals.reduce((s, g) => {
                            const gScore = g.agreed_score !== undefined && g.agreed_score !== null
                              ? Number(g.agreed_score)
                              : (Number(g.hod_score) || 0);
                            return s + gScore;
                          }, 0);
                          const kraSubtotalMetric = kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0);

                          return (
                            <React.Fragment key={kra || gidx}>
                              <tr className="table-secondary">
                                <td colSpan={7} className="fw-bold text-dark py-2">
                                  <i className="ti ti-folder me-2 text-primary"></i>
                                  KRA: {kra}
                                </td>
                              </tr>
                              {kraGoals.map((goal, idx) => {
                                const isUnreviewed = (goal.hod_score === 0 || goal.hod_score === undefined);
                                return (
                                  <tr key={goal.id || idx} className={isUnreviewed ? 'table-warning' : ''}>
                                    <td className="fw-medium">{goal.activity || '[No Activity]'}</td>
                                    <td>{goal.performance_standard !== undefined ? goal.performance_standard : '-'}</td>
                                    <td>
                                      <div
                                        className="d-inline-flex align-items-center px-2 py-1 rounded text-white fs-12 fw-semibold"
                                        style={{ backgroundColor: goal.progress_indicator || '#6c757d' }}
                                      >
                                        {goal.rag_color}
                                      </div>
                                    </td>
                                    <td className="text-center fw-semibold">{goal.metric !== undefined ? goal.metric : '%'}</td>
                                    <td className="text-center fw-semibold">{goal.hod_score !== undefined ? goal.hod_score : '-'}</td>
                                    <td className="text-center fw-semibold">{goal.agreed_score !== undefined && goal.agreed_score !== null ? goal.agreed_score : '-'}</td>
                                    <td className="text-center">
                                      <div className="d-flex align-items-center justify-content-center gap-2">
                                        {goal.flagged && (
                                          <span className="badge bg-danger-transparent text-danger d-inline-flex align-items-center gap-1">
                                            <i className="ti ti-flag-filled"></i> Flagged
                                          </span>
                                        )}

                                        {/* HOD Review Button */}
                                        {goal.hod_score === 0 || goal.hod_score === undefined ? (
                                          <button
                                            className="btn btn-sm btn-primary"
                                            onClick={() => handleReviewClick(goal)}
                                          >
                                            Review
                                          </button>
                                        ) : (
                                          <span className="badge bg-success-transparent text-success">Reviewed</span>
                                        )}

                                        {/* Agreed Review Button */}
                                        {goal.hod_score !== 0 && goal.hod_score !== undefined && (goal.agreed_score === 0 || goal.agreed_score === undefined) ? (
                                          <button
                                            className="btn btn-sm btn-indigo text-white"
                                            style={{ backgroundColor: '#4F46E5', borderColor: '#4F46E5' }}
                                            onClick={() => handleAgreedReviewClick(goal)}
                                          >
                                            Agree Score
                                          </button>
                                        ) : goal.agreed_score !== 0 && goal.agreed_score !== undefined ? (
                                          <span className="badge bg-indigo-transparent text-indigo" style={{ color: '#4338ca', backgroundColor: '#e0e7ff' }}>
                                            Agreed
                                          </span>
                                        ) : null}

                                        {/* View Details Button */}
                                        <button
                                          className="btn btn-sm btn-icon btn-light"
                                          onClick={() => handleViewClick(goal)}
                                          title="View Details"
                                        >
                                          <i className="ti ti-eye fs-16 text-muted"></i>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {/* KRA Subtotal Row */}
                              <tr className="bg-light-subtle">
                                <td colSpan={3} className="text-end fw-bold text-muted fs-13">KRA Total</td>
                                <td colSpan={3} className="text-center fw-bold text-dark fs-14">
                                  {kraSubtotalScore} / {kraSubtotalMetric}
                                </td>
                                <td></td>
                              </tr>
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer Score Summary */}
            {filteredGoals.length > 0 && (
              <div className="card-footer bg-white border-top py-3 d-flex justify-content-end">
                <div className="d-flex align-items-center gap-3 bg-light p-2 px-3 rounded border">
                  <span className="fw-bold text-dark">Final Total:</span>
                  <span className="fw-bold text-primary fs-15">{totalScore} / {totalMetric}</span>
                  <span className="badge bg-info-transparent text-info fs-13 px-2 py-1 rounded-pill">
                    {totalPercentage}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* HOD Score Modal */}
      {modalOpen && selectedGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Review Goal</h5>
                <button type="button" className="btn-close" onClick={() => setModalOpen(false)}></button>
              </div>
              <form onSubmit={handleModalSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label text-muted fs-13 mb-1">Activity / KPI</label>
                    <div className="fw-semibold text-dark fs-15">{selectedGoal.activity}</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted fs-13 mb-1">Goal Metric</label>
                    <div className="fw-semibold text-dark">{selectedGoal.metric}</div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="hod-score-input" className="form-label fw-medium">
                      HOD Score (Max: {selectedGoal.metric})
                    </label>
                    <input
                      id="hod-score-input"
                      type="number"
                      min="0"
                      max={selectedGoal.metric}
                      value={hodScore}
                      onChange={(e) => setHodScore(e.target.value)}
                      className="form-control"
                      placeholder="Enter HOD Score"
                      required
                      disabled={submitting}
                    />
                  </div>
                  {modalError && (
                    <div className="alert alert-danger py-2 fs-13 mb-0">{modalError}</div>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setModalOpen(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success d-flex align-items-center gap-2"
                    disabled={submitting}
                  >
                    {submitting && <span className="spinner-border spinner-border-sm"></span>}
                    <span>Submit Score</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Agreed Score Modal */}
      {agreedModalOpen && selectedGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Agree Final Score</h5>
                <button type="button" className="btn-close" onClick={() => setAgreedModalOpen(false)}></button>
              </div>
              <form onSubmit={handleAgreedScoreSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label text-muted fs-13 mb-1">Activity / KPI</label>
                    <div className="fw-semibold text-dark fs-15">{selectedGoal.activity}</div>
                  </div>

                  <div className="row text-center mb-3 g-2 bg-light p-2 rounded border mx-0">
                    <div className="col-4">
                      <div className="text-muted fs-12">Metric</div>
                      <div className="fw-bold text-dark">{selectedGoal.metric}</div>
                    </div>
                    <div className="col-4">
                      <div className="text-muted fs-12">Self Score</div>
                      <div className="fw-bold text-dark">{selectedGoal.score}</div>
                    </div>
                    <div className="col-4">
                      <div className="text-muted fs-12">HOD Score</div>
                      <div className="fw-bold text-dark">{selectedGoal.hod_score}</div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="agreed-score-input" className="form-label fw-medium">
                      Agreed Final Score (Max: {selectedGoal.metric})
                    </label>
                    <input
                      id="agreed-score-input"
                      type="number"
                      min="0"
                      max={selectedGoal.metric}
                      value={agreedScore}
                      onChange={(e) => setAgreedScore(e.target.value)}
                      className="form-control"
                      placeholder="Enter Agreed Score"
                      required
                      disabled={submitting}
                    />
                  </div>
                  {modalError && (
                    <div className="alert alert-danger py-2 fs-13 mb-0">{modalError}</div>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setAgreedModalOpen(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-indigo text-white d-flex align-items-center gap-2"
                    style={{ backgroundColor: '#4F46E5', borderColor: '#4F46E5' }}
                    disabled={submitting}
                  >
                    {submitting && <span className="spinner-border spinner-border-sm"></span>}
                    <span>Agree Score</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewModalOpen && viewGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Goal Details</h5>
                <button type="button" className="btn-close" onClick={() => setViewModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                {viewGoal.flagged && viewGoal.flagging_reason && (
                  <div className="alert alert-danger d-flex align-items-start gap-2 mb-3">
                    <i className="ti ti-flag-filled fs-18 mt-1"></i>
                    <div>
                      <div className="fw-bold mb-1">Flag Reason</div>
                      <div className="fs-14">{viewGoal.flagging_reason}</div>
                    </div>
                  </div>
                )}

                <div className="table-responsive border rounded">
                  <table className="table table-bordered mb-0">
                    <tbody>
                      <tr>
                        <th className="bg-light w-35 text-muted">Activity / KPI</th>
                        <td className="fw-medium">{viewGoal.activity}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Performance Standard</th>
                        <td>{viewGoal.performance_standard || '-'}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Metric</th>
                        <td>{viewGoal.metric || viewGoal.score || '-'}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Self Score</th>
                        <td>{viewGoal.score !== undefined ? viewGoal.score : '-'}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">HOD Score</th>
                        <td>{viewGoal.hod_score !== undefined ? viewGoal.hod_score : '-'}</td>
                      </tr>
                      <tr>
                        <th className="bg-light text-muted">Agreed Score</th>
                        <td>{viewGoal.agreed_score !== undefined && viewGoal.agreed_score !== null ? viewGoal.agreed_score : '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Document attachment if available */}
                {resolveDocumentUrl(viewGoal) && (
                  <div className="mt-3">
                    <a
                      href={resolveDocumentUrl(viewGoal)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                    >
                      <i className="ti ti-file-text fs-16"></i>
                      <span>View Supporting Document</span>
                    </a>
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeGoalsReviews;